/**
 * Endpoints de **Logística → Asignaciones** (`/api/logistica/asignaciones`).
 *
 * El reparto se guarda por (día, turno) con el estado completo de sus surtidores,
 * que es lo que hacía el panel anterior al detectar un cambio en cualquier
 * selector.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { Asignacion, AsignacionesRespuesta, AsignacionPayload } from './asignaciones.types';

export const asignacionesKeys = {
  todas: ['logistica', 'asignaciones'] as const,
  semana: (semana: string | undefined) => ['logistica', 'asignaciones', semana ?? 'actual'] as const,
} as const;

export const asignacionesApi = {
  listar: (semana?: string, signal?: AbortSignal): Promise<AsignacionesRespuesta> =>
    apiClient.get<AsignacionesRespuesta>(
      `/api/logistica/asignaciones${semana ? `?semana=${encodeURIComponent(semana)}` : ''}`,
      { signal },
    ),
  guardar: (payload: AsignacionPayload): Promise<{ asignacion: Asignacion; mensaje: string }> =>
    apiClient.post<{ asignacion: Asignacion; mensaje: string }>('/api/logistica/asignaciones', {
      body: payload,
    }),
};

/** Reparto de la semana pedida (sin semana, la actual). */
export function useAsignaciones(semana?: string) {
  return useQuery({
    queryKey: asignacionesKeys.semana(semana),
    queryFn: ({ signal }) => asignacionesApi.listar(semana, signal),
  });
}

/** Guarda el reparto de un turno. */
export function useGuardarAsignacion() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: asignacionesApi.guardar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: asignacionesKeys.todas }),
  });
}
